import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  PersonAdd as AddIcon,
  People as MembersIcon,
  BarChart as StatsIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../../../services/api';

// Tab components
import AddMembersTab from './AddMembersTab';
import CurrentMembersTab from './CurrentMembersTab';
import StatisticsTab from './StatisticsTab';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`member-tabpanel-${index}`}
      aria-labelledby={`member-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function MemberManagement({ serviceGroup, token, onClose }) {
  const [activeTab, setActiveTab] = useState(0);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    loadData();
  }, [serviceGroup._id, token, refreshTrigger]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [membersData, groupData] = await Promise.all([
        api.get(`/service-groups/${serviceGroup._id}/members?isActive=true`),
        api.get(`/service-groups/${serviceGroup._id}`)
      ]);

      setMembers(membersData.data.data || []);
      setStats(groupData.data.data?.stats || null);
      setError('');
    } catch (err) {
      console.error('Error loading member data:', err);
      setError(err.response?.data?.msg || 'Failed to load member data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Manage Members: {serviceGroup.name}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="member management tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<AddIcon />}
            iconPosition="start"
            label="Add Members"
            id="member-tab-0"
            aria-controls="member-tabpanel-0"
          />
          <Tab
            icon={<MembersIcon />}
            iconPosition="start"
            label="Current Members"
            id="member-tab-1"
            aria-controls="member-tabpanel-1"
          />
          <Tab
            icon={<StatsIcon />}
            iconPosition="start"
            label="Statistics"
            id="member-tab-2"
            aria-controls="member-tabpanel-2"
          />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <AddMembersTab
            serviceGroup={serviceGroup}
            token={token}
            onRefresh={handleRefresh}
            currentMembers={members}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <CurrentMembersTab
            serviceGroup={serviceGroup}
            token={token}
            members={members}
            onRefresh={handleRefresh}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <StatisticsTab
            serviceGroup={serviceGroup}
            stats={stats}
            members={members}
          />
        </TabPanel>
      </Paper>
    </Box>
  );
}

export default MemberManagement;
